// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Gamepad2, Trophy, Zap, ShieldCheck, Globe2, UserPlus } from "lucide-react";
import { sounds } from "@/lib/sounds";
import { useGeo } from "@/lib/geo";
import { getSeoConfig, applySeoHead } from "@/lib/seoKeywords";

export default function Landing({ seoSlug = null }) {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const geo = useGeo();
  const isIndia = geo.isIndia;
  // Programmatic-SEO overrides (only present on /:keyword landing routes).
  // When null we fall back to the geo-aware default hero copy that already
  // exists below — that keeps `/` untouched.
  const seo = getSeoConfig(seoSlug);

  useEffect(() => {
    if (seo) {
      // Keyword landing pages: apply the keyword's <head> tags. Skip the
      // auth-redirect-to-/game behaviour so search engines and cold visitors
      // ALWAYS see the SEO copy for the keyword URL they clicked in on. If
      // the user wants to play, the "Play with Google" / "Continue as Guest"
      // buttons still work exactly the same.
      applySeoHead(seo);
      setChecking(false);
      return;
    }
    document.title = isIndia
      ? "Chrome Dino Game Online — Play Google Dinosaur T-Rex Runner | DareDino"
      : "DareDino — Chrome Dino Endless Runner · Global Skill Leaderboard";
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get("/auth/me");
        // Defensive: Cloudflare Pages SPA fallback may return index.html with
        // status 200 when REACT_APP_BACKEND_URL is misconfigured. Only treat
        // this as an authenticated session if the payload is actually a JSON
        // user object with a user_id.
        if (res?.data && typeof res.data === "object" && res.data.user_id) {
          navigate("/game", { replace: true });
        } else {
          setChecking(false);
        }
      } catch {
        setChecking(false);
      }
    })();
  }, [navigate, isIndia, seo]);

  const handleLogin = () => {
    sounds.play("click");
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUri = window.location.origin + "/auth/google";
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "online",
      prompt: "select_account",
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleGuest = async () => {
    sounds.play("click");
    // If we've stored a previous guest identity locally, try to restore
    // that same account instead of creating a brand-new guest every time.
    // Falls through to fresh guest mode if the stored account was deleted
    // or the localStorage entry is stale.
    try {
      const storedId =
        typeof window !== "undefined"
          ? localStorage.getItem("dg_guest_user_id")
          : null;
      if (storedId) {
        await api.post("/auth/guest-restore", { user_id: storedId });
        navigate("/game", { replace: true });
        return;
      }
    } catch {
      // Stored id is stale — clear and proceed as a new guest
      try {
        localStorage.removeItem("dg_guest_user_id");
        localStorage.removeItem("dg_guest_username");
      } catch {}
    }
    navigate("/game", { state: { guestMode: true } });
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
          Booting...
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#050505] overflow-hidden">
      <div className="absolute inset-0 grid-overlay opacity-40 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#00F0FF]/40" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-[#00F0FF]/20">
        <div className="flex items-center gap-3" data-testid="site-logo">
          <div className="w-8 h-8 bg-[#EAEB18] flex items-center justify-center text-black font-black">
            D
          </div>
          <div className="font-heading font-black text-lg md:text-xl tracking-wider text-white">
            DARE<span className="text-[#EAEB18]">DINO</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 px-6 md:px-12 pt-16 pb-24 max-w-6xl">
        <div className="stagger-item text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#00F0FF] mb-6" data-testid="hero-eyebrow">
          {seo
            ? seo.eyebrow
            : isIndia
            ? "[ Skill-based Arcade // Daily Leaderboard ]"
            : "[ Skill-based Arcade // Global Leaderboard ]"}
        </div>

        {seo ? (
          // Programmatic-SEO hero — driven by /:keyword route. Same layout,
          // only the words change. H1 accent is the keyword itself so the
          // primary search term visibly pops.
          <>
            <h1
              data-testid="seo-h1-keyword"
              data-seo-slug={seo.slug}
              className="stagger-item font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white mb-4"
              style={{ animationDelay: "0.1s" }}
            >
              {seo.h1.split(seo.h1Accent).map((part, i, arr) => (
                <span key={i}>
                  {part}
                  {i < arr.length - 1 && (
                    <span className="text-[#EAEB18] glow-yellow">{seo.h1Accent}</span>
                  )}
                </span>
              ))}
            </h1>
            <h2
              data-testid="seo-h2-keyword"
              className="stagger-item font-heading font-bold text-base md:text-lg text-[#00F0FF] glow-cyan mb-6 uppercase tracking-wider"
              style={{ animationDelay: "0.15s" }}
            >
              {seo.h2}
            </h2>
            <p
              data-testid="seo-body-keyword"
              className="stagger-item text-[#E2E8F0] max-w-2xl text-sm md:text-base leading-relaxed mb-10"
              style={{ animationDelay: "0.2s" }}
            >
              {seo.body}
            </p>
          </>
        ) : isIndia ? (
          <>
            <h1
              data-testid="seo-h1"
              className="stagger-item font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white mb-4"
              style={{ animationDelay: "0.1s" }}
            >
              Chrome Dino Game Online:{" "}
              <span className="text-[#EAEB18] glow-yellow">#1 Google Dinosaur</span>{" "}
              T-Rex Runner Skill Challenge
            </h1>

            <h2
              data-testid="seo-h2"
              className="stagger-item font-heading font-bold text-base md:text-lg text-[#00F0FF] glow-cyan mb-6 uppercase tracking-wider"
              style={{ animationDelay: "0.15s" }}
            >
              Play Chrome Dinosaur Game · Dino Run · Daily Skill Leaderboard
            </h2>

            <p
              data-testid="seo-body"
              className="stagger-item text-[#E2E8F0] max-w-2xl text-sm md:text-base leading-relaxed mb-10"
              style={{ animationDelay: "0.2s" }}
            >
              DareDino is the ultimate <strong>chrome dino game</strong> reimagined
              for the web — the classic <strong>google chrome dinosaur game</strong>{" "}
              (aka the <em>no-internet game</em>, <em>T-Rex Runner</em> or{" "}
              <em>google dino</em>) built as a pure skill challenge. Jump the
              cactus, dodge the dragons, and climb the daily leaderboard. Free
              to play, unblocked, mobile friendly — the world&rsquo;s best{" "}
              <strong>dino run</strong> experience where reflexes decide the
              ranking.
            </p>
          </>
        ) : (
          <>
            <h1
              data-testid="seo-h1-global"
              className="stagger-item font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white mb-4"
              style={{ animationDelay: "0.1s" }}
            >
              Run, Dodge,{" "}
              <span className="text-[#EAEB18] glow-yellow">Dominate</span>{" "}
              the Leaderboard.
            </h1>

            <h2
              data-testid="seo-h2-global"
              className="stagger-item font-heading font-bold text-base md:text-lg text-[#00F0FF] glow-cyan mb-6 uppercase tracking-wider"
              style={{ animationDelay: "0.15s" }}
            >
              A Premium Chrome Dino Endless Runner · Live Global Rankings
            </h2>

            <p
              data-testid="seo-body-global"
              className="stagger-item text-[#E2E8F0] max-w-2xl text-sm md:text-base leading-relaxed mb-10"
              style={{ animationDelay: "0.2s" }}
            >
              DareDino is a fast, skill-based <strong>chrome dino</strong>{" "}
              endless runner rebuilt for the modern web — 60fps canvas,
              delta-timed physics, dragon obstacles, and a live global
              leaderboard that refreshes every 3 seconds. Sign in with Google
              to save your best run and climb the ranks. Free to play,
              unblocked, and fully mobile friendly.
            </p>
          </>
        )}

        <div className="stagger-item flex flex-wrap gap-4 mb-16" style={{ animationDelay: "0.3s" }}>
          <button
            data-testid="google-login-btn"
            onClick={handleLogin}
            className="btn-brutal px-8 py-4 text-sm md:text-base rounded-none flex items-center gap-3"
          >
            <Gamepad2 size={20} strokeWidth={2.5} />
            Play with Google
          </button>
          <button
            data-testid="continue-as-guest-btn"
            onClick={handleGuest}
            className="btn-ghost-cyan px-8 py-4 text-sm md:text-base rounded-none inline-flex items-center gap-2"
          >
            <UserPlus size={18} strokeWidth={2.5} />
            Continue as Guest
          </button>
        </div>

        {/* Feature grid — third card swaps between INR cash-prize (IN) and generic global ranking (rest of world) */}
        <div id="features" className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            {
              icon: Zap,
              title: "Zero-Lag Engine",
              desc: "Pure canvas. 60fps. Delta-timed loop. No frame drops.",
              color: "#EAEB18",
              testId: "feature-engine",
            },
            {
              icon: Trophy,
              title: "Live Leaderboard",
              desc: "Daily & Weekly. Top 100. Refreshes every 3 seconds.",
              color: "#00F0FF",
              testId: "feature-leaderboard",
            },
            isIndia
              ? {
                  icon: ShieldCheck,
                  title: "Daily Skill Reward",
                  desc: "Top 3 skill winners unlock UPI reward payout daily.",
                  color: "#FF007F",
                  testId: "feature-cash-prize",
                }
              : {
                  icon: Globe2,
                  title: "Global Ranking",
                  desc: "Compete worldwide. Verified anti-cheat. Zero pay-to-win.",
                  color: "#FF007F",
                  testId: "feature-global-ranking",
                },
          ].map((f, i) => (
            <div
              key={f.title}
              data-testid={f.testId}
              className="stagger-item p-6 border border-[#00F0FF]/30 bg-[#0B0C10] hover:border-[#EAEB18] transition-colors"
              style={{ animationDelay: `${0.4 + i * 0.1}s` }}
            >
              <f.icon size={28} strokeWidth={2.5} color={f.color} />
              <div className="mt-4 font-heading font-bold text-lg text-white">{f.title}</div>
              <div className="mt-2 text-xs text-[#94A3B8] leading-relaxed">{f.desc}</div>
            </div>
          ))}
        </div>
      </main>

      {/* SEO CONTENT SECTION — India-only (targets INR cash/UPI keyword cluster).
          Global users see a clean, non-promotional landing. */}
      {isIndia && (
      <section className="relative z-10 border-t border-[#00F0FF]/20 bg-[#0B0C10]/40 px-6 md:px-12 py-16" data-testid="seo-section-in">
        <div className="max-w-4xl mx-auto space-y-10">
          <div>
            <h2 className="font-heading font-black text-2xl md:text-3xl text-white mb-4">
              About the <span className="text-[#EAEB18]">Chrome Dino Game</span>
            </h2>
            <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed">
              The <strong className="text-white">Chrome Dino Game</strong> — also
              known as the <strong className="text-white">T-Rex Runner</strong>,
              the <strong className="text-white">Google Dinosaur Game</strong>,
              or simply the <em>no-internet game</em> — is the iconic endless
              runner that appears in Google Chrome when your connection drops.
              A pixel dinosaur sprints across a desert, jumping cacti and
              flying dragons (pterodactyls). DareDino is the
              enhanced, online, skill-based version of that classic{" "}
              <strong className="text-white">chrome dinosaur game</strong>{" "}
              you already love — with a live daily leaderboard and UPI skill
              rewards for the top three players.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                q: "How do I play the Chrome Dino Game online?",
                a: "Click Play with Google above. Press Space (or tap the screen) to jump over cactus obstacles and flying dragons. Survive as long as possible to climb the daily leaderboard.",
              },
              {
                q: "Is DareDino the same as the Google Chrome dinosaur game?",
                a: "Yes — DareDino is an enhanced online T-Rex Runner. Simple one-button jump mechanics like the offline chrome dino game, plus dragon obstacles, live leaderboard, and UPI skill rewards.",
              },
              {
                q: "Is the dino game free and unblocked?",
                a: "Absolutely. DareDino is a 100% free chrome dino game unblocked in the browser. No download, no install. Only the daily top 3 unlock UPI skill rewards.",
              },
              {
                q: "How does the daily UPI skill reward work?",
                a: "Daily top 3 players on the leaderboard unlock the UPI collector inside the game. Rewards are paid to your UPI ID daily and weekly based on your skill ranking — 100% skill-based, no entry fee, no betting.",
              },
              {
                q: "Does it work on mobile?",
                a: "Yes — DareDino is a fully responsive chrome dino game online. Tap the screen or swipe up to jump. Runs smoothly on phones and tablets.",
              },
              {
                q: "How is DareDino different from the offline dinosaur game?",
                a: "DareDino keeps the pure T-Rex Runner feel (60fps canvas, delta-time loop) but adds dragon obstacles, resume-with-ad on death, live leaderboards, and daily UPI skill rewards.",
              },
            ].map((item) => (
              <div key={item.q} className="border border-[#00F0FF]/25 bg-[#050505] p-5">
                <h3 className="font-heading font-bold text-sm md:text-base text-[#00F0FF] mb-2">
                  {item.q}
                </h3>
                <p className="text-xs md:text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>

          <div>
            <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-3">
              Related Searches
            </h2>
            <div className="flex flex-wrap gap-2">
              {[
                "chrome dino game",
                "chrome dinosaur game",
                "google dino game",
                "t-rex runner",
                "dino run",
                "dino game unblocked",
                "chrome dino unblocked",
                "google chrome dinosaur game",
                "no internet game",
                "offline dinosaur game",
                "dinosaur game online",
                "dino jumping game",
                "t rex chrome",
                "chrome offline game",
                "google dinosaur",
                "play dino game",
                "chrome dino run",
                "dino skill game",
              ].map((kw) => (
                <span
                  key={kw}
                  className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#94A3B8] border border-[#00F0FF]/20 px-3 py-1 bg-[#0B0C10]"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* SEO CONTENT SECTION — Global (non-India) users. Skill/gameplay focused,
          zero cash/prize keywords. Targets universal chrome-dino search intent. */}
      {!isIndia && (
      <section
        className="relative z-10 border-t border-[#00F0FF]/20 bg-[#0B0C10]/40 px-6 md:px-12 py-16"
        data-testid="seo-section-global"
      >
        <div className="max-w-4xl mx-auto space-y-10">
          <div>
            <h2 className="font-heading font-black text-2xl md:text-3xl text-white mb-4">
              About the <span className="text-[#EAEB18]">Chrome Dino Game</span>
            </h2>
            <p className="text-sm md:text-base text-[#94A3B8] leading-relaxed">
              The <strong className="text-white">Chrome Dino Game</strong> — also
              known as the <strong className="text-white">T-Rex Runner</strong>,
              the <strong className="text-white">Google Dinosaur Game</strong>,
              or simply the <em>no-internet game</em> — is the iconic endless
              runner that appears inside Google Chrome when your connection
              drops. DareDino is a modern, online rebuild of that classic{" "}
              <strong className="text-white">chrome dinosaur game</strong>{" "}
              built for competitive skill play — 60fps canvas, delta-timed
              physics, dragon obstacles, verified
              anti-cheat, and a live global leaderboard that refreshes every
              3 seconds. No pay-to-win, no downloads — just pure reflex.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                q: "How do I play the Chrome Dino Game online?",
                a: "Click Play with Google above. Press Space (or tap the screen) to jump over cactus obstacles and flying dragons. Survive as long as possible to climb the global daily and weekly leaderboards.",
              },
              {
                q: "Is DareDino the same as the Google Chrome dinosaur game?",
                a: "Yes — DareDino is an enhanced online T-Rex Runner. Same one-button jump mechanics as the offline chrome dino game, plus dragon obstacles, resume-with-ad on death, and a live global leaderboard.",
              },
              {
                q: "Is the dino game free and unblocked?",
                a: "Absolutely. DareDino is a 100% free chrome dino game unblocked in any modern browser. No download, no install, no signup wall — sign in with Google only to save your best run.",
              },
              {
                q: "How does the global leaderboard work?",
                a: "Your best run is submitted with a signed HMAC payload and cross-verified server-side (score/duration/obstacles ratios) to prevent tampering. Rankings refresh every 3 seconds — daily boards reset at 00:00 UTC, weekly boards reset every Monday 00:00 UTC.",
              },
              {
                q: "Does it work on mobile?",
                a: "Yes — DareDino is a fully responsive chrome dino game online. Tap the screen or swipe up to jump. Runs smoothly on phones and tablets at 60fps.",
              },
              {
                q: "How is DareDino different from the offline dinosaur game?",
                a: "DareDino keeps the pure T-Rex Runner feel (60fps canvas, delta-time loop) but adds dragon obstacles, resume-with-ad on death, and a live verified global skill leaderboard.",
              },
            ].map((item) => (
              <div key={item.q} className="border border-[#00F0FF]/25 bg-[#050505] p-5">
                <h3 className="font-heading font-bold text-sm md:text-base text-[#00F0FF] mb-2">
                  {item.q}
                </h3>
                <p className="text-xs md:text-sm text-[#94A3B8] leading-relaxed">{item.a}</p>
              </div>
            ))}
          </div>

          <div>
            <h2 className="font-heading font-black text-xl md:text-2xl text-white mb-3">
              Related Searches
            </h2>
            <div className="flex flex-wrap gap-2">
              {[
                "chrome dino game",
                "chrome dinosaur game",
                "google dino game",
                "t-rex runner",
                "dino run",
                "dino game unblocked",
                "chrome dino unblocked",
                "google chrome dinosaur game",
                "no internet game",
                "offline dinosaur game",
                "dinosaur game online",
                "dino jumping game",
                "t rex chrome",
                "chrome offline game",
                "google dinosaur",
                "play dino game",
                "endless runner game",
                "skill leaderboard game",
              ].map((kw) => (
                <span
                  key={kw}
                  className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-[#94A3B8] border border-[#00F0FF]/20 px-3 py-1 bg-[#0B0C10]"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>
      )}

      <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-[#00F0FF]/20" />
      <footer className="relative z-10 px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]/60" data-testid="footer-copyright">
          © DAREDINO 2026 · Skill-based{isIndia ? " · Daily Rewards" : " · Global"}
        </span>
        <nav className="flex flex-wrap gap-4 text-[10px] uppercase tracking-[0.3em]" data-testid="footer-legal-links">
          <button onClick={() => navigate("/pricing")} className="text-[#00F0FF] hover:text-white" data-testid="footer-pricing-link">Pricing</button>
          <button onClick={() => navigate("/privacy-policy")} className="text-[#94A3B8] hover:text-white" data-testid="footer-privacy-link">Privacy Policy</button>
          <button onClick={() => navigate("/terms-of-service")} className="text-[#94A3B8] hover:text-white" data-testid="footer-terms-link">Terms of Service</button>
        </nav>
      </footer>
    </div>
  );
}
